import React from "react";
import ListItemText from "@material-ui/core/ListItemText";
import ListItem from "@material-ui/core/ListItem";
import IconButton from "@material-ui/core/IconButton";
import { MoreVert } from "@material-ui/icons";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import classnames from "classnames";
import UpdateNow16 from "@carbon/icons-react/lib/update-now/16";
import Video24 from "@carbon/icons-react/lib/video/24";
import HighlightText from "./HighlightText";
import playListItemStyles from "../styles/PlayListItem.styles";
import { getSafeOrDefault } from "../../../util/object-util";
import { useTeamsContext } from "../../../state/teams/context";
import { useAppContext } from "../../../state/app/context";
import { useFootballContext } from "../../../state/football-play/context";
import { getPlayNumber, isTurnoverPlay } from "./utils/PlayUtils";
import QueryStringUtils from "../../../util/query-string-utils";

const fragmentList = (play, searchText, classes) => {
	const { messages, details } = play;
	return (
		messages && (
			<div id="fragment-list" className={classes.fragmentList}>
				{messages.map((message) => (
					<HighlightText key={message} text={message} searchText={searchText} />
				))}
				{details && details.length > 0 && (
					<ul className={classes.detailList}>
						{details.map((detail) => (
							<li key={detail}>
								<HighlightText key={details} text={detail} searchText={searchText} />
							</li>
						))}
					</ul>
				)}
			</div>
		)
	);
};

export const usePlayTeamPossession = (play) => {
	const { state: teamState } = useTeamsContext();
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();

	const homeTeamHasPossession = getSafeOrDefault(
		() => play.contextSnapshot.homeTeamHasPossession,
		null
	);

	return homeTeamHasPossession ? homeTeam : awayTeam;
};

const PlayListItem = ({
	play,
	searchText,
	actionDisabled,
	streamMarkersDisabled,
	formattedTime,
	showDriveStart,
	driveTotalTime,
	onShowAction = () => {},
	onShowStreamMarkers = () => {}
}) => {
	const { id, title, isUpdate, index, isDriveEnd, driveId } = play;
	const playNumber = getPlayNumber(index);
	const isTurnover = isTurnoverPlay(play);
	const playTeamPossession = usePlayTeamPossession(play);
	const classes = playListItemStyles({ team: playTeamPossession });
	const { setDebugData } = useAppContext();

	// Drive replay rides along with the debug switch for now.
	const isDebug = QueryStringUtils.useIsDebug();
	// Only the driveReplay selector is read here -- the walk itself is driven by
	// useDriveReplay in the replay dialog. Every row running that hook would
	// give the drive one timer per row.
	const { state: footballState, startDriveReplay } = useFootballContext();
	const driveReplay = footballState.driveReplay();
	const isBeingReplayed = !!driveId && driveReplay?.driveId === driveId;

	return (
		<ListItem
			id="gamePlaysListItem"
			button
			key={`${id}`}
			classes={{
				divider: classes.divider
			}}
			className={classnames(classes.listItem, {
				[classes.replaying]: isBeingReplayed
			})}
			divider
		>
			<ListItemText
				onClick={() => setDebugData(play || {})}
				disableTypography
				primary={
					<div className={classes.itemContent}>
						<div className={classes.itemHeader}>
							<div className={classnames(classes.playNumber)}>
								<HighlightText
									key={playNumber}
									text={playNumber}
									searchText={searchText}
									exactMatch
								/>
							</div>
							{isUpdate && (
								<div className={classnames(classes.square, classes.update)}>
									<UpdateNow16 />
								</div>
							)}
							<div className={classes.title}>
								<HighlightText key={title} text={title} searchText={searchText} />
							</div>
							<div className={classnames(classes.square, classes.time)}>
								<HighlightText
									key={formattedTime}
									text={formattedTime}
									searchText={searchText}
									exactMatch
								/>
							</div>
							<div className={classes.icons}>
								{/* On the drive-end row, not the drive-start one: isDriveEnd is
								    only stamped once the drive is no longer the active one, so
								    the button never offers to replay a drive still in progress. */}
								{isDebug && isDriveEnd && driveId && (
									<IconButton
										onClick={() => startDriveReplay(driveId)}
										aria-label="replay drive on the field"
										title="Replay this drive on the field"
										size="small"
										color="secondary"
									>
										<PlayCircleOutlineIcon />
									</IconButton>
								)}
								{!streamMarkersDisabled && (
									<IconButton
										onClick={onShowStreamMarkers}
										aria-label="playlist item stream makers"
										size="small"
										color="secondary"
									>
										<Video24 />
									</IconButton>
								)}
								{!actionDisabled && (
									<IconButton
										onClick={onShowAction}
										aria-label="playlist item action"
										size="small"
										color="secondary"
										disabled={actionDisabled}
									>
										<MoreVert />
									</IconButton>
								)}
							</div>
						</div>
					</div>
				}
				secondary={
					<>
						{fragmentList(play, searchText, classes)}
						{(showDriveStart || isDriveEnd || isTurnover) && (
							<div className={classes.driveMarkers}>
								{showDriveStart && (
									<span className={classnames(classes.driveMarker, classes.driveStart)}>
										Drive Start
									</span>
								)}
								{isTurnover && (
									<span className={classnames(classes.driveMarker, classes.turnover)}>
										Turnover
									</span>
								)}
								{isDriveEnd && (
									<span className={classnames(classes.driveMarker, classes.driveEnd)}>
										{`Drive End - Total Time: ${driveTotalTime}`}
									</span>
								)}
							</div>
						)}
					</>
				}
			/>
		</ListItem>
	);
};

export default PlayListItem;
